import axios from 'axios';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { Gizmo } from '../../gizmos';
import {
  IBinderSetDataParams,
  IError,
  IGizmoData,
  IGizmoEvent,
  TGizmoData,
} from '../../types';
import CommonBinder from '../common-binder';

interface IPhoneFieldBinder {
  init(): Promise<void>;
  setValue(value: string): Promise<void>;
  getValue(): string | undefined;
  value: Subject<string>;
}

class PhoneFieldBinder extends CommonBinder implements IPhoneFieldBinder {
  private _verificationCodeSent: boolean = false;
  private _verificationCodeVerified: boolean = false;
  private _showCodeVerification: boolean = false;
  /**
   * Observable which will be used by the UI layer in order to avoid Flowy
   * data format
   */
  public value: Subject<string>;
  public showCodeVerification: Subject<boolean>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.value = new Subject<string>();
    this.showCodeVerification = new Subject<boolean>();
    this._gizmo.data.subscribe((data: TGizmoData) => {
      if (data && !Array.isArray(data) && typeof data.v === 'string') {
        this.value.next(data.v);
      }
    });
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      // If the gizmo has no data and we have value configuration we proceed to evalute the Flux
      // script
      if (config.ops?.value?.flux && !this._gizmo.getData()) {
        const flux = this._gizmo.getFlux();

        if (flux) {
          let conditionResult = true;

          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });

            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (conditionResult === true) {
            const result = await flux.evaluate({
              script: config.ops.value.flux,
            });

            if (result && typeof result === 'string') {
              await this.setValue(result);
            }
          }
        }
      }
      resolve();
    });
  };

  /**
   * Binder specific validation
   *
   * @returns {Promise<IError[]>}
   */
  public binderValidate = (): Promise<IError[]> => {
    return new Promise<IError[]>(async resolve => {
      const config = this._gizmo.getConfig();
      const errors: IError[] = [];
      const value = this.getValue();

      // The first typeof check is to avoid errors when the value is undefined
      if (typeof value?.length === 'number' && value?.length !== 10) {
        errors.push({
          fid: uuid(),
          code: 'phone_field.format',
          message: 'El teléfono no cumple con el formato de 10 dígitos',
        });
      }

      if (
        config.ops?.phoneField?.verificationCode &&
        this._verificationCodeVerified === false
      ) {
        errors.push({
          fid: uuid(),
          code: 'phone_field.verification',
          message: 'Código de verificación incorrecto',
        });
      }

      resolve(errors);
    });
  };

  /**
   * Function used to set an answer
   * @param value value being set by the UI layer
   */
  public setValue = (value?: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      if (value !== undefined) {
        const v = value.replace(/\D/g, '');
        if (v.length === 10 && config.ops?.phoneField?.verificationCode) {
          this._showCodeVerification = true;
          this.showCodeVerification.next(true);
        }

        await this._gizmo.setData({ v });
      } else {
        await this._gizmo.setData(undefined);
      }
      resolve();
    });
  };

  /**
   * Set data to the gizmo
   * @param data gizmo data to be set
   */
  public setData = async ({ data }: IBinderSetDataParams) => {
    if (data.v && typeof data.v === 'string') {
      const v = data.v.replace(/\D/g, '');
      await this._gizmo.setData({ v });
    }
  };

  /**
   * Get text value directly as a string
   *
   * @returns {string | undefined} text value
   */
  public getValue = (): string | undefined => {
    const data = this._gizmo.getData() as IGizmoData;

    if (data?.v && typeof data.v === 'string') {
      return data.v;
    }

    return undefined;
  };

  /**
   * Get mask to be used by the UI layer
   * @returns
   */
  public getMask = (): string => {
    const config = this._gizmo.getConfig();
    let mask = '(99) 9999 9999';

    if (config.ops?.phoneField?.customMask) {
      const regex = /^[9 ()-]+$/;

      if (regex.test(config.ops.phoneField.customMask)) {
        mask = config.ops.phoneField.customMask;
      }
    }

    return mask;
  };

  /**
   * Get the showCodeVerification value
   * @returns
   */
  public getShowCodeVerification = (): boolean => {
    return this._showCodeVerification;
  };

  /**
   * Send verification code to the user
   * @returns
   */
  public sendVerificationCode = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      // TODO: add the call to the API
      this._verificationCodeSent = true;

      const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
        type: 'rootGizmoFid',
      });
      const rootGizmoDataFid = this._gizmo.getCallbacks().getInfoCB({
        type: 'rootGizmoDataFid',
      });

      const apiUrl = this._gizmo.getDao()?.getApiUrl();
      const url = `${apiUrl}/main_api/v1/form_auth/request_account_verification/${rootGizmoFid}`;

      let name = '';
      let username = '';
      if (
        config.ops?.phoneField?.verification?.nameFlux &&
        config.ops.phoneField.verification.usernameFlux
      ) {
        const flux = this._gizmo.getFlux();
        const nameResult = await flux?.evaluate({
          script: config.ops.phoneField.verification.nameFlux,
        });
        const usernameResult = await flux?.evaluate({
          script: config.ops.phoneField.verification.usernameFlux,
        });

        if (nameResult !== undefined) {
          name = nameResult;
        }
        if (usernameResult !== undefined) {
          username = usernameResult;
        }
      }

      await axios({
        url,
        method: 'POST',
        data: {
          fid: rootGizmoDataFid,
          verification_method: 'sms',
          verification_value: this.getValue(),
          name,
          username,
        },
      });

      resolve();
    });
  };

  /**
   * Verify the code provided by the user
   * @param {string} code code provided by the user
   * @returns
   */
  public verifyCode = (code: string): Promise<boolean> => {
    return new Promise<boolean>(async resolve => {
      try {
        code = code.replace(/\D/g, '');
        const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
          type: 'rootGizmoFid',
        });
        const rootGizmoDataFid = this._gizmo.getCallbacks().getInfoCB({
          type: 'rootGizmoDataFid',
        });
        const apiUrl = this._gizmo.getDao()?.getApiUrl();
        const url = `${apiUrl}/main_api/v1/form_auth/verify_account/${rootGizmoFid}`;

        const response = await axios({
          url,
          method: 'POST',
          data: {
            fid: rootGizmoDataFid,
            verification_code: code.replace(/\D/g, ''),
            verification_value: this.getValue(),
          },
        });

        if (response.status === 200) {
          // const { data } = response;
          this._verificationCodeVerified = true;

          const eventPassword: IGizmoEvent = {
            type: 'gizmo.set-data',
            src: {
              type: 'gizmo',
              gizmoUid: this._gizmo.getConfig().uid,
            },
            dest: {
              type: 'gizmo',
              gizmoFid: 'added_password',
            },
            data: {
              gizmoSetData: {
                value: code,
              },
            },
          };
          const eventPasswordConfirmation: IGizmoEvent = {
            type: 'gizmo.set-data',
            src: {
              type: 'gizmo',
              gizmoUid: this._gizmo.getConfig().uid,
            },
            dest: {
              type: 'gizmo',
              gizmoFid: 'added_password_confirmation',
            },
            data: {
              gizmoSetData: {
                value: code,
              },
            },
          };
          // We're going to send the code to be the password
          this._gizmo.getEvents().next(eventPassword);
          this._gizmo.getEvents().next(eventPasswordConfirmation);

          return resolve(true);
        }
      } catch (e) {
        return resolve(false);
      }

      resolve(false);
    });
  };
}

export default PhoneFieldBinder;
